/**
 * Schedule section — frequency, weekly days, custom interval (max 30),
 * start time (optional), timer duration (optional).
 * No start/end dates — those are for challenges only.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'

type Frequency = 'daily' | 'weekly_days' | 'custom_interval'

interface HabitFormScheduleProps {
  frequency: Frequency
  weeklyDays: number[]
  customInterval: number
  timeStart: string
  timeDuration: string
  onFrequencyChange: (v: Frequency) => void
  onWeeklyDaysChange: (v: number[]) => void
  onCustomIntervalChange: (v: number) => void
  onTimeStartChange: (v: string) => void
  onTimeDurationChange: (v: string) => void
}

export const HabitFormSchedule: React.FC<HabitFormScheduleProps> = ({
  frequency, weeklyDays, customInterval, timeStart, timeDuration,
  onFrequencyChange, onWeeklyDaysChange, onCustomIntervalChange,
  onTimeStartChange, onTimeDurationChange,
}) => {
  const { t, i18n } = useTranslation()

  const dayLabels = i18n.language === 'uz'
    ? ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya']
    : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

  const toggleDay = (day: number): void => {
    onWeeklyDaysChange(
      weeklyDays.includes(day) ? weeklyDays.filter((d) => d !== day) : [...weeklyDays, day],
    )
  }

  return (
    <>
      {/* Frequency */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-2">
          {t('home.habitForm.frequency')}
        </label>
        <div className="flex gap-2 flex-wrap">
          {(['daily', 'weekly_days', 'custom_interval'] as const).map((f) => (
            <button key={f} type="button" onClick={() => onFrequencyChange(f)}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                frequency === f
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-white/5 text-text-hint border border-border'
              }`}>
              {t(`home.habitForm.${f === 'daily' ? 'daily' : f === 'weekly_days' ? 'weeklyDays' : 'customInterval'}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Weekly day selector */}
      {frequency === 'weekly_days' && (
        <div className="flex gap-2">
          {dayLabels.map((label, i) => (
            <button key={i} type="button" onClick={() => toggleDay(i + 1)}
              className={`w-10 h-10 rounded-full text-xs font-semibold transition-all ${
                weeklyDays.includes(i + 1)
                  ? 'bg-primary text-white'
                  : 'bg-white/5 text-text-hint border border-border'
              }`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Custom interval — "Har [N] kunda" */}
      {frequency === 'custom_interval' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-hint">{t('home.habitForm.everyPrefix')}</span>
          <input type="number" min={2} max={30} value={customInterval}
            onChange={(e) => onCustomIntervalChange(Math.min(30, Math.max(2, Number(e.target.value) || 2)))}
            className="w-14 h-10 bg-white/5 border border-border rounded-xl text-center text-sm text-text outline-none focus:ring-2 focus:ring-primary" />
          <span className="text-sm text-text-hint">{t('home.habitForm.everySuffix')}</span>
        </div>
      )}

      {/* Start time — optional */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-1">
          {t('home.habitForm.startTime')}
        </label>
        <input type="time" value={timeStart} onChange={(e) => onTimeStartChange(e.target.value)}
          className="w-full h-10 bg-white/5 border border-border rounded-xl px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary" />
        <p className="text-[10px] text-text-hint/40 mt-1">{t('home.habitForm.startTimeHint')}</p>
      </div>

      {/* Timer duration — optional (minutes) */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-1">
          {t('home.habitForm.timerDuration')}
        </label>
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={480} value={timeDuration}
            onChange={(e) => onTimeDurationChange(e.target.value)}
            placeholder="0"
            className="w-20 h-10 bg-white/5 border border-border rounded-xl text-center text-sm text-text outline-none focus:ring-2 focus:ring-primary" />
          <span className="text-sm text-text-hint">{t('home.habitForm.minutes')}</span>
        </div>
        <p className="text-[10px] text-text-hint/40 mt-1">{t('home.habitForm.timerHint')}</p>
      </div>
    </>
  )
}
